import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ChevronDown } from "lucide-react";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { setAppLanguage } from "../i18n";

const LANGS = [
  { code: "sv" as const, flag: "🇸🇪", label: "Svenska" },
  { code: "en" as const, flag: "🇬🇧", label: "English" },
];

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x05}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.small}px;
  background: ${colors.background};
  color: ${colors.text};
  font-size: ${fontSizes.regular}px;
  line-height: 1;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    box-shadow 0.15s;

  &:hover {
    background-color: ${colors.backgroundDark};
    border-color: ${colors.textMuted};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  svg {
    flex-shrink: 0;
    color: ${colors.textMuted};
    transition: transform 0.2s;
  }

  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
`;

const Flag = styled.span`
  font-size: 1.25em;
  line-height: 1;
`;

const Menu = styled.div<{ $openUpward: boolean }>`
  position: absolute;
  top: ${({ $openUpward }) =>
    $openUpward ? "auto" : `calc(100% + ${spacing.x05}px)`};
  bottom: ${({ $openUpward }) =>
    $openUpward ? `calc(100% + ${spacing.x05}px)` : "auto"};
  right: 0;
  min-width: 11rem;
  padding: ${spacing.x05}px;
  background: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.small}px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  z-index: 1010;
`;

const MenuItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  width: 100%;
  padding: ${spacing.x1 + spacing.x05}px ${spacing.x2}px;
  border: none;
  border-radius: ${borderRadius.small}px;
  background: ${({ $active }) => ($active ? colors.backgroundDark : "transparent")};
  color: ${colors.text};
  font-size: ${fontSizes.small}px;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background-color: ${colors.backgroundDark};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: -2px;
  }
`;

const MenuFlag = styled.span`
  font-size: 1.35em;
  line-height: 1;
`;

export function LanguageToggle({ openUpward = false }: { openUpward?: boolean }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const lng = i18n.language.startsWith("en") ? "en" : "sv";
  const current = LANGS.find((l) => l.code === lng) ?? LANGS[0];

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const pick = (code: "sv" | "en") => {
    setAppLanguage(code).then(() => {
      setOpen(false);
    });
  };

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`${t("language.chooseLanguage")}: ${current.label}`}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <Flag aria-hidden>{current.flag}</Flag>
        <ChevronDown size={16} strokeWidth={2} aria-hidden />
      </Trigger>
      {open && (
        <Menu role="menu" aria-label={t("language.chooseLanguage")} $openUpward={openUpward}>
          {LANGS.map((item) => (
            <MenuItem
              key={item.code}
              type="button"
              role="menuitem"
              $active={item.code === lng}
              onClick={() => {
                pick(item.code);
              }}
            >
              <MenuFlag aria-hidden>{item.flag}</MenuFlag>
              <span>{item.label}</span>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Wrap>
  );
}
